import { useRef, useState } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, logSession, LogCategory, LogLevel } from "utils/sentryLogger";

export const useSessionAnalysis = () => {
  const [transcriptData, setTranscriptData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  // Refs, not the loading state: a collapse-and-reopen while the first read is
  // still out lands before that state does, and re-issued the slow request.
  // Keyed by session so the admin analyzer can still switch sessions mid-read.
  const transcriptInFlight = useRef(null);
  const summaryInFlight = useRef(null);

  const fetchTranscript = async (sessionId) => {
    if (!sessionId || transcriptInFlight.current === sessionId) return;

    transcriptInFlight.current = sessionId;
    setIsLoadingTranscript(true);
    setTranscriptError(null);

    try {
      const response = await request(`session_summary/${sessionId}/transcript`, {
        method: "GET",
      });

      if (response?.error === true) {
        const errorMessage = response.error_message || "Failed to load transcript data.";
        setTranscriptError(errorMessage);

        console.error('Transcript API returned error:', {
          error_type: response.error_type,
          error_message: response.error_message,
          session_id: response.session_id
        });

        logError("Transcript generation error", null, {
          category: LogCategory.SESSION,
          errorType: response.error_type || 'unknown',
          errorMessage: response.error_message,
          sessionId: response.session_id,
          technicalDetails: response.technical_details,
        });

        const error = new Error(errorMessage);
        error.detail = errorMessage;
        error.errorType = response.error_type;
        throw error;
      }

      setTranscriptData(response);
    } catch (e) {
      console.error('Error fetching transcript:', e);
      if (e?.status !== 404) {
        logError("Failed to fetch session transcript", e, {
          category: LogCategory.SESSION,
          sessionId,
        });
      }
      const errorMessage = e?.detail || e?.message || "Failed to load transcript data.";
      setTranscriptError(errorMessage);
      throw e;
    } finally {
      if (transcriptInFlight.current === sessionId) transcriptInFlight.current = null;
      setIsLoadingTranscript(false);
    }
  };

  const fetchSummary = async (sessionId) => {
    if (!sessionId || summaryInFlight.current === sessionId) return;

    summaryInFlight.current = sessionId;
    setIsLoadingSummary(true);
    setSummaryError(null);

    try {
      const response = await request(`session_summary/${sessionId}/summary`, {
        method: "GET",
      });

      if (response?.error === true) {
        const errorMessage = response.error_message || "Failed to load summary data.";
        setSummaryError(errorMessage);

        console.error('Summary API returned error:', {
          error_type: response.error_type,
          error_message: response.error_message,
          session_id: response.session_id
        });

        logSession("Summary generation error", {
          level: LogLevel.WARNING,
          sessionId: response.session_id,
          errorType: response.error_type || 'unknown',
          errorMessage: response.error_message,
          technicalDetails: response.technical_details,
        });

        const error = new Error(errorMessage);
        error.detail = errorMessage;
        error.errorType = response.error_type;
        throw error;
      }

      setSummaryData(response);
    } catch (e) {
      console.error('Error fetching summary:', e);
      if (e?.status === 404 || e?.detail?.includes("Session analysis not found")) {
        return;
      }
      const errorMessage = e?.detail || e?.message || "Failed to load summary data.";
      setSummaryError(errorMessage);
      throw e;
    } finally {
      if (summaryInFlight.current === sessionId) summaryInFlight.current = null;
      setIsLoadingSummary(false);
    }
  };

  const clearTranscript = () => {
    setTranscriptData(null);
    setTranscriptError(null);
  };

  const clearSummary = () => {
    setSummaryData(null);
    setSummaryError(null);
  };

  const clearAll = () => {
    clearTranscript();
    clearSummary();
  };

  return {
    transcriptData,
    summaryData,
    isLoadingTranscript,
    isLoadingSummary,
    transcriptError,
    summaryError,
    fetchTranscript,
    fetchSummary,
    clearTranscript,
    clearSummary,
    clearAll,
  };
};
